import {z} from 'zod';
import {zodTypeguard} from '../api/helpers';
import {ObjectIdSchema} from '../zod';
import {BaseFieldDefinitionSchema} from './base';
import {ObjectId} from 'bson';
import {IntentSource} from '../intent';
import {assertNever} from '../util';
import _ from 'lodash';

export const IntentFieldIntervalSchema = z.union([
  z.literal(30),
  z.literal(90),
  z.literal(180),
]);
export type IntentFieldInterval = z.infer<typeof IntentFieldIntervalSchema>;

const BaseIntentFieldDefinitionSchema = BaseFieldDefinitionSchema.extend({
  type: z.literal('intent'),
  interval: IntentFieldIntervalSchema,
  dataType: z.literal('number'),
  lastUpdated: z.date().optional(),
});

// LinkedIn intent field types
export const LinkedInIntentFieldTypes = [
  'clicks',
  'reactions',
  'impressions',
] as const;
export const LinkedInIntentFieldTypeSchema = z.enum(LinkedInIntentFieldTypes);
export type LinkedInIntentFieldType = z.infer<
  typeof LinkedInIntentFieldTypeSchema
>;

export const LinkedInIntentFieldDefinitionSchema =
  BaseIntentFieldDefinitionSchema.extend({
    source: z.literal('linkedIn'),
    linkedInField: LinkedInIntentFieldTypeSchema,
    adAccountId: z.number(),
  });
export type LinkedInIntentFieldDefinition = z.infer<
  typeof LinkedInIntentFieldDefinitionSchema
>;
export const isLinkedInIntentFieldDefinition = zodTypeguard(
  LinkedInIntentFieldDefinitionSchema
);

export const AggregatedIntentFieldDefinitionSchema =
  BaseIntentFieldDefinitionSchema.extend({
    source: z.literal('aggregated'),
    sourceId: ObjectIdSchema,
  });
export type AggregatedIntentFieldDefinition = z.infer<
  typeof AggregatedIntentFieldDefinitionSchema
>;
export const isAggregatedIntentFieldDefinition = zodTypeguard(
  AggregatedIntentFieldDefinitionSchema
);

export const IntentFieldDefinitionSchema = z.discriminatedUnion('source', [
  LinkedInIntentFieldDefinitionSchema,
  AggregatedIntentFieldDefinitionSchema,
]);
export const isIntentFieldDefinition = zodTypeguard(
  IntentFieldDefinitionSchema
);
export type IntentFieldDefinition = z.infer<typeof IntentFieldDefinitionSchema>;

export const LinkedInFieldTypeLabels: Record<LinkedInIntentFieldType, string> =
  {
    clicks: 'Clicks',
    reactions: 'Reactions',
    impressions: 'Impressions',
  };

export function formatIntentFieldName(
  label: string,
  interval: IntentFieldInterval
) {
  return `${label} L${interval}d`;
}

export function generateIntentFieldName(
  options:
    | {
        type: 'linkedIn';
        linkedInField?: LinkedInIntentFieldType;
        interval?: IntentFieldInterval;
      }
    | {
        type: 'aggregated';
        intentSources: IntentSource[];
        sourceId?: ObjectId;
        interval?: IntentFieldInterval;
      }
) {
  if (!options.interval) {
    return '';
  }

  switch (options.type) {
    case 'linkedIn': {
      const {linkedInField, interval} = options;
      if (!linkedInField) {
        return '';
      }

      const fieldTypeLabel = LinkedInFieldTypeLabels[linkedInField];
      return formatIntentFieldName(`LinkedIn ${fieldTypeLabel}`, interval);
    }

    case 'aggregated': {
      const {interval, intentSources, sourceId} = options;
      const intentSource = _.find(intentSources, ({id}) =>
        _.isEqual(id, sourceId)
      );
      if (!intentSource) {
        return '';
      }

      return formatIntentFieldName(intentSource.label, interval);
    }

    default:
      assertNever(options);
  }
}
